import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme, createStyles, Typography } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import PanelOpenIcon from '../../../../../static/outageIconsV2/left_panel_open.svg';
import PanelCloseIcon from '../../../../../static/outageIconsV2/left_panel_close.svg';
import PublicLeftPanelContent from '../../public-left-panel-content/PublicLeftPanelContent';
import { useOutageLeftPanelState } from '../../../providers/OutageLeftPanelStateProvider';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { useIsMobile } from '../../../../../util/style-utils';
import SecureLeftPanelContent from '../../secure-left-panel-content/SecureLeftPanelContent';
import useAuth from '../../../../../hooks/useAuth';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../../providers/OutageResponsivePageTracker';
import mainColors from '../../../../../themes/main-colors';

const drawerWidth = 340;
const miniDrawerWidth = 80;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      '& .MuiIconButton-root': {
        padding: 0,
      },
    },
    drawerOpen: {
      top: 'auto',
      position: 'relative',
      height: 'calc(100vh - 72px)',
      width: drawerWidth,
      backgroundColor: mainColors.riverBlue,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      overflowX: 'hidden',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    drawerClose: {
      top: 'auto',
      position: 'relative',
      height: 'calc(100vh - 72px)',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: miniDrawerWidth,
      [theme.breakpoints.down('sm')]: {
        width: 0,
      },
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 2.5),
      cursor: 'pointer',
      backgroundColor: mainColors.white,
      '& p': {
        fontSize: '1rem',
      },
    },
    toolbarClose: {
      justifyContent: 'center',
    },
  }),
);

export default function OutageLeftPanel() {
  const classes = useStyles();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { isAuthenticated } = useAuth();
  const { panelOpen, setPanelOpen } = useOutageLeftPanelState();
  const { page } = useOutageResponsivePageTracker();

  const handleDrawerToggle = () => {
    setPanelOpen(!panelOpen);
  };

  if (isMobile && page === ResponsivePageView.Map) {
    return null;
  }

  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: panelOpen,
        [classes.drawerClose]: !panelOpen,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: panelOpen,
          [classes.drawerClose]: !panelOpen,
        }),
      }}
    >
      {!isMobile && (
        <div
          className={clsx(classes.toolbar, {
            [classes.toolbarClose]: !panelOpen,
          })}
          onClick={handleDrawerToggle}
        >
          {panelOpen && (
            <Typography variant={'body1'}>{t('CLOSE_PANEL')}</Typography>
          )}
          <IconButton>
            {panelOpen ? <PanelOpenIcon /> : <PanelCloseIcon />}
          </IconButton>
        </div>
      )}
      {isAuthenticated ? (
        <SecureLeftPanelContent />
      ) : (
        <PublicLeftPanelContent />
      )}
    </Drawer>
  );
}
